/**
 * «Аномалии» раздела EO «Виджет» (01.11, ТЗ 1.17 EMB-019 ч. 5, §0.6 п. 70; INTERNAL 0.5 §6.3): ряды по суткам (7 дней) из ответа
 * GET /api/widget-sites/v2/anomalies и всплески по порогам. ПОРОГИ — ТОЛЬКО ЗДЕСЬ (ANOMALY), тест — widgetAnomaly.test.ts.
 * Всплеск — сутки, где событий не меньше min и не меньше factor × медианы остальных суток окна (нет истории — медиана 0 → порог
 * min и factor). alert — какая тревога V4 уже ловит это сама (INTERNAL §7, OPS-011), null — тревоги нет, только экран.
 * Сеть — только хеш (16 hex, соль суток экземпляра): сравним лишь в пределах суток и экземпляра.
 */
import type { Anomalies } from "./widgetTypes";

export interface Rule { min: number; factor: number; label: string; alert: string | null }
export const ANOMALY = {
  limited: { min: 20, factor: 3, label: "429 прокладки (лимит)", alert: "pow_switch — PoW auto включается при > 20 отказов по лимиту за 10 мин" },
  refused_origin: { min: 20, factor: 3, label: "отказ: чужой Origin", alert: null },
  refused_key: { min: 10, factor: 3, label: "отказ: чужой или отозванный ключ", alert: null },
  suspect: { min: 5, factor: 3, label: "заявки «под вопросом» (прокладка)", alert: "pow_switch — PoW auto включается при > 5 таких за 10 мин" },
  schema: { min: 1, factor: 1, label: "ответ V4 не по схеме", alert: "up_schema — любое нарушение за 15 мин" },
  hp: { min: 3, factor: 3, label: "ловушка (hp)", alert: null },
  v4_limited: { min: 10, factor: 3, label: "429 V4 (лимит заявок)", alert: null },
  v4_invalid: { min: 20, factor: 3, label: "400 V4 (заявка не по формату)", alert: null },
  v4_suspect: { min: 5, factor: 3, label: "заявки track:false в V4", alert: null },
  net: { min: 20, factor: 1, label: "события одной сети за сутки", alert: null },
} as const satisfies Record<string, Rule>;
export type RuleName = keyof typeof ANOMALY;

const median = (xs: number[]): number => {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b), m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
};
/** Всплеск в сутки i ряда values по правилу rule (см. шапку). */
export function isSpike(values: readonly number[], i: number, rule: Rule): boolean {
  const v = values[i] ?? 0, base = median(values.filter((_, j) => j !== i));
  return v >= rule.min && v >= rule.factor * Math.max(1, base);
}

export interface Series { key: string; rule: RuleName; scope: string; detail: string; values: number[]; spikes: boolean[]; total: number }
const series = (days: readonly string[], key: string, rule: RuleName, scope: string, detail: string, pts: { day: string; n: number }[]): Series => {
  const values = days.map((d) => pts.filter((p) => p.day === d).reduce((a, p) => a + p.n, 0));
  return { key, rule, scope, detail, values, spikes: values.map((_, i) => isSpike(values, i, ANOMALY[rule])), total: values.reduce((a, b) => a + b, 0) };
};

/** Ряды по экземплярам из сводок прокладки и по сайтам из своих счётчиков V4; пустые ряды не показываются. siteName — домен по id. */
export function anomalySeries(a: Anomalies, siteName: (id: number) => string): Series[] {
  const out: Series[] = [];
  const insts = [...new Set(a.proxy.map((r) => r.instance))].sort();
  for (const inst of insts) {
    const rows = a.proxy.filter((r) => r.instance === inst);
    const pick = (f: (r: (typeof rows)[number]) => boolean) => rows.filter(f).map((r) => ({ day: r.day, n: r.n }));
    out.push(series(a.days, `${inst}:limited`, "limited", `экземпляр ${inst}`, "", pick((r) => r.kind === "limited")));
    out.push(series(a.days, `${inst}:origin`, "refused_origin", `экземпляр ${inst}`, "", pick((r) => r.kind === "refused" && r.sub === "origin")));
    out.push(series(a.days, `${inst}:key`, "refused_key", `экземпляр ${inst}`, "", pick((r) => r.kind === "refused" && r.sub === "key")));
    for (const why of [...new Set(rows.filter((r) => r.kind === "suspect").map((r) => r.k))].sort()) {
      out.push(series(a.days, `${inst}:suspect:${why}`, "suspect", `экземпляр ${inst}`, why, pick((r) => r.kind === "suspect" && r.k === why)));
    }
    for (const path of [...new Set(rows.filter((r) => r.kind === "schema").map((r) => r.k))].sort()) {
      out.push(series(a.days, `${inst}:schema:${path}`, "schema", `экземпляр ${inst}`, path, pick((r) => r.kind === "schema" && r.k === path)));
    }
  }
  for (const id of [...new Set((a.v4 ?? []).map((r) => r.site_id))].sort((x, y) => x - y)) {
    const rows = (a.v4 ?? []).filter((r) => r.site_id === id), scope = siteName(id);
    const pick = (kind: string) => rows.filter((r) => r.kind === kind).map((r) => ({ day: r.day, n: r.n }));
    out.push(series(a.days, `${id}:hp`, "hp", scope, "", pick("hp")));
    out.push(series(a.days, `${id}:limited`, "v4_limited", scope, "", pick("limited")));
    out.push(series(a.days, `${id}:invalid`, "v4_invalid", scope, "", pick("invalid")));   // отказы 400 — поток мусорных POST (ревью b235d4e4)
    for (const k of [...new Set(rows.filter((r) => r.kind.startsWith("suspect:")).map((r) => r.kind))].sort()) {
      out.push(series(a.days, `${id}:${k}`, "v4_suspect", scope, k.slice(8), pick(k)));
    }
  }
  return out.filter((s) => s.total > 0);
}

export interface NetRow { day: string; instance: string; net: string; total: number; limited: number; origin: number; key: number; suspect: number; spike: boolean }
/** Сети по суткам и экземплярам (сервер отдал до 10 на сутки): всплеск — событий не меньше ANOMALY.net.min. «_other» — сумма прочих. */
export function netRows(a: Anomalies): NetRow[] {
  return a.nets.map((r) => {
    const total = r.limited + r.origin + r.key + r.suspect;
    return { ...r, total, spike: r.net !== "_other" && total >= ANOMALY.net.min };
  }).sort((x, y) => (x.day === y.day ? y.total - x.total : x.day < y.day ? 1 : -1));
}
